import {
  IconActivity,
  IconBatteryCharging,
  IconBook2,
  IconHeartbeat,
  IconHome,
  IconKey,
  IconLock,
  IconMoon,
  IconMovie,
  IconNotebook,
  IconPlugConnected,
  IconRadar,
  IconSettings,
  IconShare,
  IconUser,
  IconUserCircle,
  IconUsersGroup,
  IconWorld,
  type Icon as TablerIcon,
} from '@tabler/icons-react'

/**
 * The navigation registry: one declaration for the whole two-level nav.
 *
 * The outer rail lists SECTIONS (Personal, Pages, Marketing, Settings); the inner menu
 * lists the selected section's ITEMS. Rail, inner menu and active-section
 * resolution all read this table — a new destination is one entry here, not
 * three hand-kept lists that drift apart.
 *
 * Items may carry `children` — rendered as a collapsible sub-menu in the
 * sidebar (SidebarMenuSub). A parent with children has no `url` of its own;
 * the children are the destinations.
 */
export type SectionId = 'personal' | 'market' | 'marketing' | 'settings'

export interface NavSubItem {
  title: string
  url: string
  icon: TablerIcon
}

export interface NavItem {
  title: string
  url?: string
  /** Match this route exactly — for `/`, which prefixes everything. */
  end?: boolean
  icon: TablerIcon
  children?: NavSubItem[]
}

export interface NavSection {
  id: SectionId
  label: string
  icon: TablerIcon
  items: NavItem[]
  /** Not a tile in the rail's list: reached from the gear at the rail's foot. */
  atFoot?: boolean
}

export const NAV_SECTIONS: NavSection[] = [
  {
    id: 'personal',
    label: 'Personal',
    icon: IconUser,
    items: [
      { title: 'Overview', url: '/', end: true, icon: IconHome },
      {
        title: 'Health',
        icon: IconHeartbeat,
        children: [
          { title: 'Sleep', url: '/health/sleep', icon: IconMoon },
          { title: 'Activity', url: '/health/activity', icon: IconActivity },
          { title: 'Heart', url: '/health/heart', icon: IconHeartbeat },
          { title: 'Body', url: '/health/body', icon: IconBatteryCharging },
        ],
      },
    ],
  },
  {
    id: 'market',
    label: 'Pages',
    icon: IconBook2,
    items: [{ title: 'Pages', url: '/pages', icon: IconNotebook }],
  },
  {
    id: 'marketing',
    label: 'Marketing',
    icon: IconWorld,
    items: [
      { title: 'Audience', url: '/audiences', icon: IconUsersGroup },
      { title: 'Reels', url: '/reels', icon: IconMovie },
      { title: 'Social', url: '/social', icon: IconShare },
      { title: 'Signals', url: '/signals', icon: IconRadar },
    ],
  },
  {
    // How the account is wired to the outside world. Each entry is its own
    // page; a bare /settings redirects to the first. The rail shows it as the
    // gear at its foot (components/rail-settings.tsx), not in its list.
    id: 'settings',
    label: 'Settings',
    icon: IconSettings,
    atFoot: true,
    items: [
      { title: 'Account', url: '/settings/accounts', icon: IconUserCircle },
      { title: 'Agentic access', url: '/settings/agentic-access', icon: IconKey },
      { title: 'Connectors', url: '/settings/connectors', icon: IconPlugConnected },
      { title: 'Secrets', url: '/settings/secrets', icon: IconLock },
    ],
  },
]

/**
 * Which section owns a route. `undefined` for routes no section claims
 * (unknown paths) — the caller keeps whatever section was already
 * selected, so opening one never yanks the rail's highlight around.
 */
export function sectionForPath(pathname: string): SectionId | undefined {
  for (const section of NAV_SECTIONS) {
    for (const item of section.items) {
      if (item.url) {
        if (
          item.end
            ? pathname === item.url
            : pathname === item.url || pathname.startsWith(`${item.url}/`)
        ) {
          return section.id
        }
      }
      if (item.children) {
        for (const child of item.children) {
          if (pathname === child.url || pathname.startsWith(`${child.url}/`)) {
            return section.id
          }
        }
      }
    }
  }
  return undefined
}

/**
 * The rail's memory. The shell is mounted PER PAGE, so React state alone
 * forgets the selection on every navigation — landing on /nothing-here from a
 * Marketing page would snap the rail back to Personal, which is exactly the
 * yank `sectionForPath`'s `undefined` exists to prevent. Module state
 * outlives the shell; a full reload forgetting it is fine, a click is not.
 */
let remembered: SectionId = 'personal'

export function rememberSection(id: SectionId): void {
  remembered = id
}

/** The section the rail should light: the route's owner, else the last one lit. */
export function resolveSection(pathname: string): SectionId {
  return sectionForPath(pathname) ?? remembered
}
